import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { Checkbox, Grid, Tooltip, Typography } from '@mui/material';
import AutocompleteComponent from '../../../components/layout/Autocomplete';
import { useTranslation } from 'react-i18next';
import useContracts from '../../../components/layout/ContractSelector/hook/useContracts';
import {
    useGetTodosLosEstablecimientosQuery,
    useLazyGetCitiesQuery,
    useGetProvincesQuery
} from '../../../services/companyApi';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { useGetPreventorsQuery, useGetTiposVisitasQuery } from '../../../services/visitasApi';
import { useGetProvidersQuery } from '../../../services/providersApi';
import { Dispatch, SetStateAction } from 'react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { VisitaEstado, VisitStatus } from '../consts/visitasStatus';
import { VISIT_TYPE_DISPLAY_NAME_OVERRIDES } from '../consts/visitTypeDisplayNames';
import { useRoles } from '../../../hooks/useRoles';
import { useOrganizationFilter } from '../../../contexts/OrganizationFilterContext';
import { useAppDispatch, useAppSelector } from '../../../hooks/reduxHooks';
import { selectCurrentUser } from '../../../features/auth/authSlice';
import { setSelectedOrganizationId } from '../../../redux/slices/organizationsSlice';
import { VISIT_TABS_INDEX } from '../../../redux/slices/visitsSlice';
import { useGetOrganismsQuery } from '../../../services/organismsApi';
import useOrganizationSelector from '../../../components/layout/OrganizationSelector/hook/useOrganizationSelector';
import { formatFullName } from '../../../utils/common';
import { commonInputStyle } from '../../../components/CustomFormsComponents/CustomField';
import { buildVisitYearOptions } from '../../../utils/visitYearOptions';
import { ESTABLISHMENT_STATE } from '../../Empresas/components/EstablishmentStateBadge';
import useAsignacionSupervisor from '../../../hooks/useAsignacionSupervisor';

const EXCLUDED_CUITS = ['30500052089', '30707519092'];
const ALL_STATES_OPTION_ID = '__ALL_STATES__';

interface VisitasFiltersProps {
    extraFilters: any;
    setExtraFilters: Dispatch<SetStateAction<any>>;
    handleChangeExtraFilters: (e: any) => void;
    isShowMoreFiltersOpen?: boolean;
    selectedEstablishment: any;
}

const VisitasFilters = ({
    extraFilters = {},
    handleChangeExtraFilters,
    isShowMoreFiltersOpen = false,
    selectedEstablishment
}: VisitasFiltersProps) => {
    const { t } = useTranslation('visitas');
    const currentTabIndex = useAppSelector((state) => state.visits.currentTab);
    const { contracts, isFetching: isFetchingContracts } = useContracts();
    const { isPerfilBackOffice, isAdminBO, isPreventor, isProvider, isSupervisorSIMP } =
        useRoles();
    const currentUser = useAppSelector(selectCurrentUser);
    const asignacion = useAsignacionSupervisor();
    const { selectedOrganization, setSelectedOrganization } = useOrganizationFilter();
    const [allOrganisms, setAllOrganisms] = useState<any[]>([]);
    const [filteredOrganisms, setFilteredOrganisms] = useState<any[]>([]);
    const [filteredEstablishments, setFilteredEstablishments] = useState<any[]>([]);
    const handleChangeExtraFiltersRef = useRef(handleChangeExtraFilters);

    useEffect(() => {
        handleChangeExtraFiltersRef.current = handleChangeExtraFilters;
    }, [handleChangeExtraFilters]);

    const { data: preventors, isFetching: isFetchingPreventors } = useGetPreventorsQuery({
        take: 9999999,
        skip: 0
    });

    const preventorOptions =
        preventors?.data?.map((p: any) => ({
            id: p.idUsuario,
            nombre: formatFullName(p.nombre, p.apellido),
            value: p.idUsuario
        })) || [];

    const { data: providersData, isFetching: isFetchingProviders } = useGetProvidersQuery({
        activo: true,
        take: 999999,
        skip: 0
    });

    const providerOptions =
        providersData?.data?.data?.map((provider: any) => ({
            id: provider.id,
            nombre: provider.razonSocial,
            value: provider.id
        })) || [];

    const yearOptions = useMemo(() => buildVisitYearOptions(), []);

    const { organizations: organizationsOptions, isFetching: isFetchingOrganizations } =
        useOrganizationSelector();
    const dispatch = useAppDispatch();

    const filters = extraFilters || {};

    const hasDateRange = Boolean(filters?.fechaInicio || filters?.fechaFin);
    const hasVisitDate = Boolean(filters?.fechaVisita);
    const hasYear = Boolean(filters?.anioVisita);

    const showDateRangeFilter =
        currentTabIndex === VISIT_TABS_INDEX.ALL ||
        currentTabIndex === VISIT_TABS_INDEX.IN_REVISION ||
        currentTabIndex === VISIT_TABS_INDEX.CLOSED;

    const toDateKey = (value: any) => (value && dayjs(value).isValid() ? dayjs(value).toISOString() : null);

    const [draftFechaInicio, setDraftFechaInicio] = useState<any>(filters?.fechaInicio ?? null);
    const [draftFechaFin, setDraftFechaFin] = useState<any>(filters?.fechaFin ?? null);

    // El filtro va por id de organismo central y no por nro de contrato: varios
    // organismos comparten el mismo número (p. ej. 10000) y el filtro mostraba y
    // traía otro contrato (SMP-451).
    // El supervisor solo ofrece los contratos de sus organismos asignados, que
    // vienen en el usuario logueado (SMP-459).
    const contractOptions = useMemo(
        () =>
            (isSupervisorSIMP ? asignacion.contratos : contracts).map((contract) => ({
                nombre: `${contract.nombre}`,
                id: contract.id,
                value: contract.id
            })),
        [contracts, asignacion.contratos, isSupervisorSIMP]
    );

    const idOrganizacionFromContract = filters?.idOrganismoCentral || undefined;

    const {
        data: { data: { data: organisms } } = { data: { data: [] } },
        isFetching: isFetchingOrganisms
    } = useGetOrganismsQuery({
        idOrganizacion: idOrganizacionFromContract || undefined,
        skip: 0,
        take: 999
    });

    // `multiple` va a la versión 2 del listado, que no excluye los establecimientos
    // dados de baja: sus visitas cerradas se siguen buscando por establecimiento
    // (SMP-513). Esa versión toma los filtros en plural (`ids...`) y aplica la misma
    // visibilidad por perfil.
    const empresasQueryParams = {
        multiple: true,
        idsOrganizacion: selectedOrganization?.id || undefined,
        idsContrato: idOrganizacionFromContract || undefined,
        idsOrganismo: extraFilters?.idOrganismo || undefined
    };

    // Trae todos los establecimientos de los filtros, sin un `take` fijo: el
    // autocompletar busca solo entre lo que trae esta consulta, y con 1000 los
    // más nuevos de un contrato grande quedaban afuera (SMP-450).
    const { data: establishmentsData, isFetching: isFetchingEstablishments } =
        useGetTodosLosEstablecimientosQuery(empresasQueryParams);

    // La versión 2 trae también los rechazados (alta no aprobada), que la versión
    // anterior excluía y no tienen visitas.
    const establishmentOptions = useMemo(
        () =>
            establishmentsData?.data?.data
                ?.filter((est: any) => est?.idEstado !== ESTABLISHMENT_STATE.RECHAZADO)
                .map((est: any) => ({
                    id: est.id,
                    nombre: `${est.nombre} (${t('filters.srtCode')}: ${est.codigoSRT})`,
                    value: est.id,
                    idOrganismo: est?.organismo?.id,
                    nombreParam: est.nombre,
                    codigoSRT: est.codigoSRT
                })) || [],
        [establishmentsData?.data?.data, t]
    );

    const { data: provincesList } = useGetProvincesQuery(null);
    const provinces =
        provincesList?.data?.provincias?.map((province: { id: number; nombre: string }) => ({
            nombre: province.nombre,
            id: province.id,
            value: province.id
        })) || [];

    const [getCities, { data: citiesList }] = useLazyGetCitiesQuery();

    useEffect(() => {
        if (filters?.idProvincia) {
            getCities({ provinceId: filters.idProvincia });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.idProvincia]);

    useEffect(() => {
        if (!filters.idProvincia && !filters.idLocalidad) return;

        handleChangeExtraFiltersRef.current({
            target: { name: 'idLocalidad', value: null }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.idProvincia]);

    useEffect(() => {
        if (!filters.anioVisita || (!filters.fechaVisita && !filters.fechaInicio && !filters.fechaFin))
            return;

        // Solo puede haber un filtro de fecha activo a la vez: si se elige
        // Fecha de Visita o un rango Fecha desde/hasta, se limpia el Año.
        handleChangeExtraFiltersRef.current({
            target: { name: 'anioVisita', value: null }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.fechaVisita, filters.fechaInicio, filters.fechaFin]);

    useEffect(() => {
        if (showDateRangeFilter) return;
        if (!filters.fechaInicio && !filters.fechaFin) return;

        // En las pestañas donde Fecha desde/hasta no se muestra, se limpia el
        // valor para no seguir mandándolo en los GET de esa pestaña.
        handleChangeExtraFiltersRef.current({ target: { name: 'fechaInicio', value: null } });
        handleChangeExtraFiltersRef.current({ target: { name: 'fechaFin', value: null } });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showDateRangeFilter]);

    // Si el rango se limpia/cambia desde afuera (Limpiar filtros, cambio de
    // pestaña) sincronizamos el borrador para no dejar valores stale en los
    // DatePicker de Fecha desde/hasta.
    useEffect(() => {
        setDraftFechaInicio(filters?.fechaInicio ?? null);
        setDraftFechaFin(filters?.fechaFin ?? null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters?.fechaInicio, filters?.fechaFin]);

    // Fecha desde/hasta solo se aplican cuando el rango está completo: evita
    // disparar una búsqueda con un solo extremo cargado.
    useEffect(() => {
        const startKey = toDateKey(draftFechaInicio);
        const endKey = toDateKey(draftFechaFin);
        const committedStartKey = toDateKey(filters?.fechaInicio);
        const committedEndKey = toDateKey(filters?.fechaFin);

        if (startKey === committedStartKey && endKey === committedEndKey) return;

        if (startKey && endKey) {
            handleChangeExtraFiltersRef.current({ target: { name: 'fechaVisita', value: null } });
            handleChangeExtraFiltersRef.current({ target: { name: 'anioVisita', value: null } });
            handleChangeExtraFiltersRef.current({ target: { name: 'fechaInicio', value: draftFechaInicio } });
            handleChangeExtraFiltersRef.current({ target: { name: 'fechaFin', value: draftFechaFin } });
        } else if (committedStartKey || committedEndKey) {
            handleChangeExtraFiltersRef.current({ target: { name: 'fechaInicio', value: null } });
            handleChangeExtraFiltersRef.current({ target: { name: 'fechaFin', value: null } });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draftFechaInicio, draftFechaFin]);

    const cities = Array.isArray(citiesList?.data?.localidades)
        ? citiesList.data.localidades.map(
              (city: { id: number; nombre: string; codigoPostal: number }) => ({
                  nombre: `${city.nombre} - ${city.codigoPostal}`,
                  id: city.id,
                  value: city.id
              })
          )
        : [];

    useEffect(() => {
        if (!extraFilters?.idOrganismoCentral && !filters?.idEstablecimiento) return;

        handleChangeExtraFiltersRef.current({
            target: {
                name: 'idEstablecimiento',
                value: null,
                id: null
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [extraFilters?.idOrganismoCentral]);

    useEffect(() => {
        if (
            isProvider &&
            currentUser?.proveedor?.id &&
            filters?.idProveedor !== currentUser.proveedor.id
        ) {
            handleChangeExtraFiltersRef.current({
                target: { name: 'idProveedor', value: currentUser.proveedor.id }
            });
        }
    }, [isProvider, currentUser?.proveedor?.id, filters?.idProveedor]);

    useEffect(() => {
        // El contexto/Redux siempre deben guardar el id crudo (nunca el objeto completo):
        // otros módulos leen `selectedOrganization` asumiendo que es un número.
        const selectedOrganizationId =
            typeof selectedOrganization === 'object'
                ? selectedOrganization?.id ?? null
                : selectedOrganization;

        if (selectedOrganizationId && filters?.idOrganizacion !== selectedOrganizationId) {
            handleChangeExtraFiltersRef.current({
                target: {
                    name: 'idOrganizacion',
                    value: selectedOrganizationId
                }
            });
        }

        dispatch(setSelectedOrganizationId(selectedOrganizationId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOrganization]);

    const handleOrganizationChange = useCallback(
        (value: any) => {
            setSelectedOrganization(value?.id ?? null);
            handleChangeExtraFiltersRef.current({
                target: { name: 'idOrganizacion', value: value?.id || null }
            });
            handleChangeExtraFiltersRef.current({
                target: { name: 'idOrganismoCentral', value: null }
            });
        },
        [setSelectedOrganization]
    );

    const { data: tiposVisitasData } = useGetTiposVisitasQuery(null);

    const tipoVisitaOptions =
        tiposVisitasData?.data?.map((tipo: any) => ({
            id: tipo.id,
            nombre: VISIT_TYPE_DISPLAY_NAME_OVERRIDES[tipo.id] ?? tipo.descripcion?.toUpperCase(),
            value: tipo.id
        })) || [];

    const estadoOptions = Object.entries(VisitStatus)
        .filter(([key]) => Number(key) !== VisitaEstado.INFORMADO_SRT) //este estado antes era BORRADOR
        .map(([key, value]) => ({
            id: Number(key),
            nombre: value,
            value: Number(key)
        }));

    const allStatesOption = {
        id: ALL_STATES_OPTION_ID,
        nombre: t('filters.customOptions.allStates'),
        value: undefined
    };

    const preventorFilteredEstadoOptions = estadoOptions.filter(({ id }) => {
        switch (currentTabIndex) {
            case VISIT_TABS_INDEX.PENDING:
                return id === VisitaEstado.PENDIENTE;
            case VISIT_TABS_INDEX.PROGRAMMED:
                return id === VisitaEstado.PROGRAMADA;
            case VISIT_TABS_INDEX.IN_PROGRESS:
                return id === VisitaEstado.EN_PROCESO;
            case VISIT_TABS_INDEX.IN_REVISION:
                return id === VisitaEstado.REALIZADA;
            case VISIT_TABS_INDEX.CLOSED:
              return (
                    id === VisitaEstado.FALLIDA ||
                    id === VisitaEstado.PRESENTADO ||
                    id === VisitaEstado.CANCELADA
                );
            default:
                return true;
        }
    });

    const adminBoFilteredEstadoOptions = estadoOptions.filter(({ id }) => {
        switch (currentTabIndex) {
            case VISIT_TABS_INDEX.ALL:
                return true;
            case VISIT_TABS_INDEX.REJECTED:
                return id === VisitaEstado.SIN_ASIGNAR || id === VisitaEstado.RECHAZADO;
            case VISIT_TABS_INDEX.PENDING:
                return id === VisitaEstado.PENDIENTE;
            case VISIT_TABS_INDEX.PROGRAMMED:
                return id === VisitaEstado.PROGRAMADA;
            case VISIT_TABS_INDEX.IN_PROGRESS:
                return id === VisitaEstado.EN_PROCESO;
            case VISIT_TABS_INDEX.IN_REVISION:
                return id === VisitaEstado.REALIZADA;
            case VISIT_TABS_INDEX.CLOSED:
                return (
                    id === VisitaEstado.FALLIDA ||
                    id === VisitaEstado.PRESENTADO ||
                    id === VisitaEstado.CANCELADA
                );
            default:
                return true;
        }
    });

    // El supervisor solo ve visitas Presentadas: el estado queda fijo (SMP-459).
    const filteredEstadoOptions = isSupervisorSIMP
        ? estadoOptions.filter(({ id }) => id === VisitaEstado.PRESENTADO)
        : isAdminBO || isProvider
        ? adminBoFilteredEstadoOptions
        : preventorFilteredEstadoOptions;

    const estadoFilterOptions =
        (isAdminBO || isProvider) && currentTabIndex === VISIT_TABS_INDEX.ALL
            ? [allStatesOption, ...filteredEstadoOptions]
            : filteredEstadoOptions;

    useEffect(() => {
        if (
            filters?.estado !== undefined &&
            !filteredEstadoOptions.some((option) => option.id === filters.estado)
        ) {
            handleChangeExtraFiltersRef.current({
                target: { name: 'estado', value: undefined }
            });
        }
    }, [currentTabIndex, filteredEstadoOptions, filters?.estado]);

    useEffect(() => {
        if (selectedEstablishment && organizationsOptions?.length > 0) {
            // Set organization (client)
            const organizationToSet = organizationsOptions?.find(
                (org: any) => org.id === selectedEstablishment?.cliente?.id
            );
            if (organizationToSet && organizationToSet.id !== selectedOrganization?.id) {
                handleOrganizationChange(organizationToSet);
            }
        }
    }, [
        handleOrganizationChange,
        organizationsOptions,
        selectedEstablishment,
        selectedOrganization?.id
    ]);

    useEffect(() => {
        if (selectedEstablishment) {
            const contractToSet = contractOptions.find(
                (contract) =>
                    contract.value === selectedEstablishment?.organismo?.organismoCentral?.id
            );
            if (contractToSet && contractToSet.value !== filters?.idOrganismoCentral)
                handleChangeExtraFiltersRef.current({
                    target: {
                        name: 'idOrganismoCentral',
                        value: contractToSet.value
                    }
                });
        }
    }, [contractOptions, filters?.idOrganismoCentral, selectedEstablishment]);

    useEffect(() => {
        if (selectedEstablishment) {
            const establishmentToSet = filteredEstablishments.find(
                (establishment: any) => establishment.id === selectedEstablishment?.id
            );
            if (establishmentToSet && establishmentToSet.id !== filters?.idEstablecimiento)
                handleChangeExtraFiltersRef.current({
                    target: {
                        name: 'idEstablecimiento',
                        value: establishmentToSet?.id || ''
                    }
                });
        }
    }, [filteredEstablishments, filters?.idEstablecimiento, selectedEstablishment]);

    useEffect(() => {
        if (filters?.idEstablecimiento) {
            handleChangeExtraFiltersRef.current({
                target: {
                    name: 'nombreEstablecimiento',
                    value:
                        filteredEstablishments.find(
                            (est: any) => est.id === filters.idEstablecimiento
                        )?.nombreParam || ''
                }
            });
        } else if (filters?.nombreEstablecimiento) {
            // Si se limpia el establecimiento hay que limpiar también este filtro,
            // sino el backend lo sigue aplicando como búsqueda por nombre aunque
            // el campo ya se vea vacío en el front.
            handleChangeExtraFiltersRef.current({
                target: { name: 'nombreEstablecimiento', value: null }
            });
        }
    }, [filteredEstablishments, filters?.idEstablecimiento, filters?.nombreEstablecimiento]);

    useEffect(() => {
        if (idOrganizacionFromContract) {
            const filtered = allOrganisms.filter(
                (organismo) => organismo.idOrganismoCentral === idOrganizacionFromContract
            );
            setFilteredOrganisms(filtered);

            handleChangeExtraFiltersRef.current({ target: { name: 'idOrganismo', value: null } });
        } else {
            setFilteredOrganisms(allOrganisms);
        }
    }, [idOrganizacionFromContract, allOrganisms]);

    useEffect(() => {
        setFilteredEstablishments(establishmentOptions);
    }, [establishmentOptions]);

    useEffect(() => {
        if (organisms && organisms?.length > 0) {
            const formattedOrganisms = organisms
                .filter((organismo: any) => !EXCLUDED_CUITS.includes(organismo.cuit))
                .map((organismo: any) => ({
                    label: `${organismo.cuit} - ${organismo.nombre}`,
                    value: organismo.id,
                    contrato: organismo.nroContrato,
                    idOrganismoCentral: organismo?.idOrganismoCentral
                }));
            setAllOrganisms(formattedOrganisms);
        }
    }, [organisms]);

    // Supervisor: Contrato y Organismo solo ofrecen lo asignado, y si es uno solo va precargado
    // y grisado. Se vuelve a precargar después de "Limpiar" (SMP-636)
    const organismOptions = useMemo(
        () =>
            isSupervisorSIMP
                ? asignacion.organismos
                      .filter(
                          (o) =>
                              !idOrganizacionFromContract ||
                              o.idOrganismoCentral === idOrganizacionFromContract
                      )
                      .map(
                          (o) =>
                              allOrganisms.find((a) => a.value === o.id) ?? {
                                  label: o.nombre,
                                  value: o.id,
                                  idOrganismoCentral: o.idOrganismoCentral
                              }
                      )
                : filteredOrganisms,
        [
            isSupervisorSIMP,
            asignacion.organismos,
            idOrganizacionFromContract,
            allOrganisms,
            filteredOrganisms
        ]
    );
    const contratoFijo = isSupervisorSIMP && asignacion.contratos.length <= 1;
    const organismoFijo = isSupervisorSIMP && asignacion.organismos.length <= 1;

    useEffect(() => {
        if (!isSupervisorSIMP) return;
        const precargas: Array<[string, number | undefined]> = [
            ['idOrganismoCentral', asignacion.idContratoUnico],
            ['idOrganismo', asignacion.idOrganismoUnico]
        ];
        precargas.forEach(([name, value]) => {
            if (value && extraFilters?.[name] !== value) {
                handleChangeExtraFiltersRef.current({ target: { name, value } });
            }
        });
    }, [
        isSupervisorSIMP,
        asignacion.idContratoUnico,
        asignacion.idOrganismoUnico,
        extraFilters
    ]);

    return (
        <Grid container spacing={1}>
            {/* El cliente del Supervisor es siempre el suyo: fijo y grisado (SMP-636) */}
            {isSupervisorSIMP && (
                <Grid item xs={12} sm={6} md={3}>
                    <AutocompleteComponent
                        value={asignacion.cliente}
                        options={asignacion.cliente ? [asignacion.cliente] : []}
                        size="small"
                        name="idOrganizacion"
                        label={t('filters.organization')}
                        templateLabel={(option: any) => `${option?.nombre}`}
                        onChange={() => undefined}
                        disabled
                    />
                </Grid>
            )}

            {isPerfilBackOffice && !isPreventor && (
                <Grid item xs={12} sm={6} md={3}>
                    <AutocompleteComponent
                        value={
                            organizationsOptions.find(
                                (opt: any) =>
                                    opt.id === selectedOrganization?.id ||
                                    opt.id === extraFilters?.idOrganizacion
                            ) || null
                        }
                        options={organizationsOptions || []}
                        size="small"
                        name="idOrganizacion"
                        loading={isFetchingOrganizations}
                        label={t('filters.organization')}
                        templateLabel={(option: any) => {
                            return `${option?.nombre}`;
                        }}
                        onChange={handleOrganizationChange}
                        disabled={!!selectedEstablishment || isFetchingOrganizations}
                        clearable
                    />
                </Grid>
            )}

            <Grid item xs={12} sm={isPreventor ? 4 : 6} md={isPreventor ? 4 : 3}>
                <AutocompleteComponent
                    label={t('filters.contract')}
                    size="small"
                    name="idOrganismoCentral"
                    onChange={(value: any) => {
                        handleChangeExtraFilters({
                            target: { name: 'idOrganismoCentral', value: value?.value || null }
                        });
                    }}
                    value={
                        contractOptions.find((opt) => opt.value === filters?.idOrganismoCentral) ||
                        null
                    }
                    options={contractOptions}
                    loading={isFetchingContracts}
                    disabled={!!selectedEstablishment || isFetchingContracts || contratoFijo}
                    clearable={!contratoFijo}
                />
            </Grid>
            <Grid item xs={12} sm={isPreventor ? 4 : 3} md={isPreventor ? 4 : 3}>
                <AutocompleteComponent
                    name="idOrganismo"
                    label={t('filters.organism')}
                    value={
                        organismOptions.find(
                            (option) => option.value === extraFilters?.idOrganismo
                        ) || null
                    }
                    options={organismOptions}
                    templateLabel={(option: any) => option?.label || ''}
                    onChange={(value: any) => {
                        handleChangeExtraFilters({
                            target: { name: 'idOrganismo', value: value?.value || null }
                        });
                        handleChangeExtraFilters({
                            target: { name: 'idEstablecimiento', value: null }
                        });
                    }}
                    loading={isFetchingOrganisms}
                    disabled={isFetchingOrganisms || organismoFijo}
                />
            </Grid>
            <Grid item xs={12} sm={isPreventor ? 4 : 6} md={isPreventor ? 4 : 3}>
                <AutocompleteComponent
                    label={t('filters.establishment')}
                    size="small"
                    name="idEstablecimiento"
                    onChange={(value: any) =>
                        handleChangeExtraFilters({
                            target: { name: 'idEstablecimiento', value: value?.id || null }
                        })
                    }
                    value={
                        filteredEstablishments.find(
                            (opt: any) => opt.id === filters?.idEstablecimiento
                        ) || null
                    }
                    options={filteredEstablishments}
                    loading={isFetchingEstablishments}
                    disabled={isFetchingEstablishments}
                    clearable
                />
            </Grid>

            {isShowMoreFiltersOpen && (
                <>
                    <Grid item xs={12} sm={6} md={isAdminBO ? 3 : 4}>
                        <AutocompleteComponent
                            label={t('filters.province')}
                            size="small"
                            name="idProvincia"
                            onChange={(value: any) =>
                                handleChangeExtraFilters({
                                    target: {
                                        name: 'idProvincia',
                                        value: value?.id || null
                                    }
                                })
                            }
                            value={
                                provinces.find((opt: any) => opt.id === filters.idProvincia) || null
                            }
                            options={provinces}
                            clearable
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={isAdminBO ? 3 : 4}>
                        <AutocompleteComponent
                            label={t('filters.city')}
                            size="small"
                            name="idLocalidad"
                            onChange={(value: any) =>
                                handleChangeExtraFilters({
                                    target: { name: 'idLocalidad', value: value?.id || null }
                                })
                            }
                            value={cities.find((c: any) => c.id === filters.idLocalidad) || null}
                            options={cities}
                            clearable
                            disabled={!filters.idProvincia}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={isAdminBO ? 3 : 4}>
                        <Tooltip
                            title={
                                hasDateRange || hasYear ? t('filters.onlyOneDateFilterTooltip') : ''
                            }
                        >
                            <span>
                                <LocalizationProvider adapterLocale="es" dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label={t('filters.visitDate')}
                                        value={extraFilters?.fechaVisita || null}
                                        disabled={hasDateRange || hasYear}
                                        onChange={(newDate) => {
                                            if (newDate) {
                                                handleChangeExtraFilters({
                                                    target: { name: 'fechaInicio', value: null }
                                                });
                                                handleChangeExtraFilters({
                                                    target: { name: 'fechaFin', value: null }
                                                });
                                                handleChangeExtraFilters({
                                                    target: { name: 'anioVisita', value: null }
                                                });
                                            }
                                            handleChangeExtraFilters({
                                                target: { name: 'fechaVisita', value: newDate }
                                            });
                                        }}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                fullWidth: true,
                                                sx: { ...commonInputStyle }
                                            },
                                            field: {
                                                clearable: true,
                                                onClear: () =>
                                                    handleChangeExtraFilters({
                                                        target: { name: 'fechaVisita', value: null }
                                                    })
                                            }
                                        }}
                                        format="DD/MM/YYYY"
                                    />
                                </LocalizationProvider>
                            </span>
                        </Tooltip>
                    </Grid>

                    {(isAdminBO || isProvider) && (
                        <Grid item xs={12} sm={6} md={isAdminBO ? 3 : 4}>
                            <AutocompleteComponent
                                label={t('filters.preventor')}
                                size="small"
                                name="idPreventor"
                                onChange={(value: any) => {
                                    handleChangeExtraFilters({
                                        target: {
                                            name: 'idPreventor',
                                            value: value?.id || null
                                        }
                                    });
                                }}
                                value={
                                    preventorOptions.find(
                                        (opt: any) => opt.id === filters?.idPreventor
                                    ) || null
                                }
                                options={preventorOptions}
                                templateLabel={(option: any) => option?.nombre}
                                clearable
                            />
                        </Grid>
                    )}
                    {(isAdminBO || isProvider) && (
                        <Grid item xs={12} sm={6} md={isAdminBO ? 3 : 4}>
                            <AutocompleteComponent
                                label={t('filters.consultora')}
                                size="small"
                                name="idProveedor"
                                onChange={(value: any) => {
                                    handleChangeExtraFilters({
                                        target: {
                                            name: 'idProveedor',
                                            value: value?.id || null
                                        }
                                    });
                                }}
                                value={
                                    (isProvider && currentUser?.proveedor
                                        ? {
                                              id: currentUser.proveedor.id,
                                              nombre: currentUser.proveedor.razonSocial
                                          }
                                        : providerOptions.find(
                                              (opt: any) => opt.id === filters?.idProveedor
                                          )) || null
                                }
                                options={providerOptions}
                                templateLabel={(option: any) => option?.nombre}
                                loading={isFetchingProviders}
                                disabled={isProvider}
                                clearable={!isProvider}
                            />
                        </Grid>
                    )}

                    <Grid item xs={12} sm={6} md={isAdminBO ? 3 : 4}>
                        <Tooltip
                            title={
                                hasVisitDate || hasDateRange
                                    ? t('filters.onlyOneDateFilterTooltip')
                                    : ''
                            }
                        >
                            <span>
                                <AutocompleteComponent
                                    label={t('filters.anio')}
                                    size="small"
                                    name="anioVisita"
                                    onChange={(value: any) => {
                                        if (value?.id) {
                                            handleChangeExtraFilters({
                                                target: { name: 'fechaVisita', value: null }
                                            });
                                            handleChangeExtraFilters({
                                                target: { name: 'fechaInicio', value: null }
                                            });
                                            handleChangeExtraFilters({
                                                target: { name: 'fechaFin', value: null }
                                            });
                                        }
                                        handleChangeExtraFilters({
                                            target: { name: 'anioVisita', value: value?.id || null }
                                        });
                                    }}
                                    value={
                                        yearOptions.find((opt) => opt.id === filters?.anioVisita) ||
                                        null
                                    }
                                    options={yearOptions}
                                    disabled={hasVisitDate || hasDateRange}
                                    clearable
                                />
                            </span>
                        </Tooltip>
                    </Grid>

                    <Grid item xs={12} sm={6} md={isAdminBO ? 3 : 4}>
                        <AutocompleteComponent
                            label={t('filters.state')}
                            size="small"
                            name="estado"
                            onChange={(value: any) =>
                                handleChangeExtraFilters({
                                    target: {
                                        name: 'estado',
                                        value:
                                            value?.id === ALL_STATES_OPTION_ID
                                                ? undefined
                                                : value?.id
                                    }
                                })
                            }
                            value={
                                estadoFilterOptions.find(
                                    (opt: any) => opt.id === filters?.estado
                                ) ||
                                (isAdminBO && currentTabIndex === VISIT_TABS_INDEX.ALL
                                    ? allStatesOption
                                    : null) ||
                                (estadoFilterOptions?.length === 1
                                    ? estadoFilterOptions[0]
                                    : null)
                            }
                            options={estadoFilterOptions}
                            disabled={estadoFilterOptions?.length === 1}
                            clearable
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={isAdminBO ? 3 : 4}>
                        <AutocompleteComponent
                            label={t('filters.typeVisit')}
                            size="small"
                            name="idTipoVisita"
                            onChange={(value: any) =>
                                handleChangeExtraFilters({
                                    target: {
                                        name: 'idTipoVisita',
                                        value: value?.id === '' ? undefined : value?.id
                                    }
                                })
                            }
                            value={
                                tipoVisitaOptions.find(
                                    (opt: any) => opt.id === filters?.idTipoVisita
                                ) || null
                            }
                            options={tipoVisitaOptions}
                            clearable
                        />
                    </Grid>

                    {/* Fecha desde/hasta solo tienen sentido en las pestañas que agrupan
                        visitas de distintos momentos (Todas, En revisión, Cerradas); en el
                        resto de las pestañas el estado ya acota la fecha relevante.
                        Van agrupadas en un unico item para que el grid las empuje siempre
                        juntas a la fila siguiente si no entran, en vez de partirlas en dos
                        filas distintas (pasaba con el rol Proveedor). */}
                    {showDateRangeFilter && (
                    <Grid item xs={12} sm={12} md={isAdminBO ? 6 : 8}>
                        <Grid container spacing={1}>
                            <Grid item xs={12} sm={6}>
                                <Tooltip
                                    title={
                                        hasVisitDate || hasYear
                                            ? t('filters.onlyOneDateFilterTooltip')
                                            : ''
                                    }
                                >
                                    <span>
                                        <LocalizationProvider adapterLocale="es" dateAdapter={AdapterDayjs}>
                                            <DatePicker
                                                label={t('filters.startDate')}
                                                value={draftFechaInicio || null}
                                                disabled={hasVisitDate || hasYear}
                                                onChange={(newDate) => setDraftFechaInicio(newDate)}
                                                disableFuture={true}
                                                maxDate={draftFechaFin || undefined}
                                                slotProps={{
                                                    textField: {
                                                        size: 'small',
                                                        fullWidth: true,
                                                        sx: { ...commonInputStyle }
                                                    },
                                                    field: {
                                                        clearable: true,
                                                        onClear: () => setDraftFechaInicio(null)
                                                    }
                                                }}
                                                format="DD/MM/YYYY"
                                            />
                                        </LocalizationProvider>
                                    </span>
                                </Tooltip>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Tooltip
                                    title={
                                        hasVisitDate || hasYear
                                            ? t('filters.onlyOneDateFilterTooltip')
                                            : ''
                                    }
                                >
                                    <span>
                                        <LocalizationProvider adapterLocale="es" dateAdapter={AdapterDayjs}>
                                            <DatePicker
                                                label={t('filters.endDate')}
                                                value={draftFechaFin || null}
                                                disabled={hasVisitDate || hasYear}
                                                onChange={(newDate) => setDraftFechaFin(newDate)}
                                                disableFuture={true}
                                                minDate={draftFechaInicio || undefined}
                                                slotProps={{
                                                    textField: {
                                                        size: 'small',
                                                        fullWidth: true,
                                                        sx: { ...commonInputStyle }
                                                    },
                                                    field: {
                                                        clearable: true,
                                                        onClear: () => setDraftFechaFin(null)
                                                    }
                                                }}
                                                format="DD/MM/YYYY"
                                            />
                                        </LocalizationProvider>
                                    </span>
                                </Tooltip>
                            </Grid>
                        </Grid>
                    </Grid>
                    )}

                    <Grid item xs={12} sm={6} md={isAdminBO ? 3 : 4}>
                        <Grid
                            container
                            alignItems="center"
                            columnGap={1}
                            sx={{
                                height: 40,
                                flexWrap: 'nowrap',
                                '& .MuiCheckbox-root': {
                                    padding: 0.5
                                }
                            }}
                        >
                            <Grid item>
                                <Grid container alignItems="center" wrap="nowrap">
                                    <Grid item>
                                        <Checkbox
                                            name="vencidas"
                                            checked={!!filters?.vencidas}
                                            onChange={(
                                                event: React.ChangeEvent<HTMLInputElement>,
                                                value
                                            ) => {
                                                handleChangeExtraFilters({
                                                    target: {
                                                        name: 'vencidas',
                                                        value: !!value
                                                    }
                                                });
                                            }}
                                        />
                                    </Grid>
                                    <Grid item>
                                        <Typography>Vencidas</Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item>
                                <Grid container alignItems="center" wrap="nowrap">
                                    <Grid item>
                                        <Checkbox
                                            name="proximoVencer"
                                            checked={!!filters?.proximoVencer}
                                            onChange={(
                                                event: React.ChangeEvent<HTMLInputElement>,
                                                value
                                            ) => {
                                                handleChangeExtraFilters({
                                                    target: {
                                                        name: 'proximoVencer',
                                                        value: !!value
                                                    }
                                                });
                                            }}
                                        />
                                    </Grid>
                                    <Grid item>
                                        <Typography>Por vencer</Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </>
            )}
        </Grid>
    );
};

export default VisitasFilters;
